import { DEBUG_MODE } from '../spdConfig';

import { MySound } from './sound';
import * as Configuration from './testConfiguration';

export class Speaker {
  protected static defaultValume = 0.1;
  protected ON = true;
  constructor(
    protected lange: Configuration.Lange = Configuration.Lange.PL,
    protected volume: number = Speaker.defaultValume,
  ) {
    this.Init();
  }
  public isON() {
    return this.ON;
  }

  public setLange(lange: Configuration.Lange) {
    this.lange = lange;
  }

  public setVolume(volume: number = Speaker.defaultValume) {
    this.volume = volume;
  }

  public switchOff() {
    this.ON = false;
    this.stopSoundOrSpeakText();
  }

  public switchOnOff() {
    this.ON = !this.ON;

    if (!this.ON) {
      this.stopSoundOrSpeakText();
    }
  }

  protected stopSpeaking() {
    const synth = window.speechSynthesis;
    if (synth.speaking) {
      DEBUG_MODE && console.log('speechSynthesis.speaking and cancel');
      synth.cancel();
    }
  }

  private voicesReady?: Promise<SpeechSynthesisVoice[]>;

  private ensureVoices(): Promise<SpeechSynthesisVoice[]> {
    if (this.voicesReady) {
      return this.voicesReady;
    }

    const synth = window.speechSynthesis;

    this.voicesReady = new Promise(resolve => {
      const existing = synth.getVoices();
      if (existing.length) {
        resolve(existing);
      } else {
        synth.addEventListener(
          'voiceschanged',
          () => resolve(synth.getVoices()),
          { once: true },
        );
      }
    });

    return this.voicesReady;
  }

  // eslint-disable-next-line complexity
  private langPrefix(lange: Configuration.Lange): string {
    switch (lange) {
      case Configuration.Lange.PL:
        return 'pl';
      case Configuration.Lange.EN:
        return 'en';
      case Configuration.Lange.UA:
        return 'uk';
      case Configuration.Lange.RU:
        return 'ru';
      case Configuration.Lange.GE:
        return 'ka'; // Georgian
      case Configuration.Lange.TR:
        return 'tr';
      case Configuration.Lange.TJ:
        return 'tg'; // Tajik
      case Configuration.Lange.BY:
        return 'be'; // Belarusian
      case Configuration.Lange.UZ:
        return 'uz';
      case Configuration.Lange.DE:
        return 'de';
      case Configuration.Lange.AR:
        return 'ar';
      case Configuration.Lange.SQ:
        return 'sq'; // Albanian
      case Configuration.Lange.SK:
        return 'sk';
      case Configuration.Lange.LT:
        return 'lt';
      case Configuration.Lange.ES:
        return 'es';
      case Configuration.Lange.FR:
        return 'fr';
      case Configuration.Lange.PH:
        return 'fil'; /// lub 'tl'
      case Configuration.Lange.VN:
        return 'vi';
      case Configuration.Lange.RO:
        return 'ro';
      case Configuration.Lange.HI:
        return 'hi';
      default:
        return ''; // nie powinno się zdarzyć
    }
  }

  // eslint-disable-next-line complexity
  protected async selectLang(
    lange?: Configuration.Lange,
  ): Promise<SpeechSynthesisVoice | null> {
    const fallbackVoices: Record<Configuration.Lange, string[]> = {
      // cyrylica ⇒ rosyjski
      [Configuration.Lange.UA]: ['ru'],
      [Configuration.Lange.BY]: ['ru'],
      [Configuration.Lange.TJ]: ['ru'],
      [Configuration.Lange.UZ]: ['ru'],
      [Configuration.Lange.GE]: ['ge'],

      [Configuration.Lange.TR]: ['tr'],
      [Configuration.Lange.SQ]: ['sq'],

      [Configuration.Lange.SK]: ['pl'],
      [Configuration.Lange.LT]: ['lt'],
      [Configuration.Lange.PL]: ['pl'],
      [Configuration.Lange.EN]: ['en'],
      [Configuration.Lange.RU]: ['ru'],
      [Configuration.Lange.DE]: ['de'],
      [Configuration.Lange.AR]: ['ar'],
      [Configuration.Lange.ES]: ['es'],
      [Configuration.Lange.FR]: ['fr'],
      [Configuration.Lange.VN]: ['vn'],
      [Configuration.Lange.PH]: ['en'],
      [Configuration.Lange.RO]: ['it'],
      [Configuration.Lange.HI]: ['en'],
    };
    const effectiveLang = lange ?? this.lange;

    const voices = await this.ensureVoices();
    if (!voices.length) {
      return null;
    }

    const prefix = this.langPrefix(effectiveLang).toLowerCase();

    // Głos pasujący do prefiksu („pl”, „en” …)
    const candidates = voices.filter(v =>
      v.lang?.toLowerCase().startsWith(prefix),
    );
    let preferred: SpeechSynthesisVoice | null =
      candidates.find(v => v.localService) ?? candidates[0];

    if (!preferred) {
      // Fallback: głos domyślny lub pierwszy z listy
      const fallbacks = fallbackVoices[effectiveLang] ?? [];
      for (const fbPrefix of fallbacks) {
        preferred =
          voices.find(v => v.lang?.toLowerCase().startsWith(fbPrefix)) ?? null;
      }
    }
    /* kod który pozwala wybrać domyślnego lektora - polskiego ale to nie jest dobre rozwiązanie by czytał np. tureckie teksty
    if (!preferred) {
      preferred = voices.find(v => v.default) ?? voices[0] ?? null;
    }
    */

    DEBUG_MODE && console.log('selectLang', lange, effectiveLang, preferred);
    return preferred;
  }

  public stopSoundOrSpeakText() {
    MySound.stopSounds();
    this.stopSpeaking();
  }

  public async playSoundOrSpeakText(
    inputTxt: string,
    must_read = false,
    src?: string,
  ) {
    if (!this.ON && !must_read) {
      return;
    }
    this.stopSoundOrSpeakText();
    if (src) {
      await MySound.play(src, this.volume);
    } else {
      await this.speakText(inputTxt, must_read);
    }
  }

  private speakWithTimeout(
    utter: SpeechSynthesisUtterance,
    handshakeMs = 4000, // czekamy WYŁĄCZNIE na start
  ): Promise<boolean> {
    const synth = window.speechSynthesis;
    if (utter.voice?.localService === false) {
      handshakeMs = 8000;
    } // cloud

    return new Promise(resolve => {
      let started = false;

      /* watchdog startu */
      const handshakeTimer = setTimeout(() => {
        if (!started) {
          // nie ruszył wcale
          synth.cancel();
          resolve(false);
        }
      }, handshakeMs);

      /* polling .speaking */
      const poll = setInterval(() => {
        if (synth.speaking) {
          markStarted();
        }
      }, 100);

      /* wspólna funkcja */
      const markStarted = () => {
        if (started) {
          return;
        }
        started = true;

        clearTimeout(handshakeTimer); // ← wyłącz bezpiecznik
        clearInterval(poll); // ← koniec pollingu
        DEBUG_MODE && console.log('speakWithTimeout - audio started');
      };

      const finish = (ok: boolean) => {
        clearTimeout(handshakeTimer);
        clearInterval(poll);
        resolve(ok);
      };

      utter.onstart = markStarted;
      utter.onboundary = markStarted;
      utter.onend = () => {
        DEBUG_MODE && console.log('SpeechSynthesisUtterance.onend');
        finish(true);
      };
      utter.onerror = e => {
        console.error('SpeechSynthesisUtterance.onerror:', e.error);
        finish(false);
      };

      synth.speak(utter);
    });
  }

  // eslint-disable-next-line complexity
  public async speakText(
    inputTxt: string,
    must_read = false,
    lange?: Configuration.Lange,
    callback?: () => void,
  ): Promise<boolean> {
    if (!this.ON && !must_read) {
      callback?.();
      return false; //  zawsze coś zwracamy
    }

    const synth = window.speechSynthesis;
    DEBUG_MODE && console.log('speechSynthesis is called');

    if (synth.speaking || synth.pending) {
      DEBUG_MODE && console.log('speechSynthesis.speaking - cancel previous');
      synth.cancel();
    }

    // najpierw sprawdź dostępny głos

    const voice = await this.selectLang(lange);
    if (!voice) {
      DEBUG_MODE && console.log('No voice for:', lange ?? this.lange);
      callback?.();
      return false;
    }

    if (!inputTxt.trim()) {
      callback?.();
      return false;
    }

    /* --- Utterance -------------------------------------------------- */
    const utter = new SpeechSynthesisUtterance(inputTxt);
    utter.voice = voice;
    utter.lang = voice.lang; // KLUCZOWE — zgodność voice/lang
    utter.pitch = 1;
    utter.rate = 1.2;
    utter.volume = Math.max(0, Math.min(this.volume, 1)); // Ustawienie głośności, ograniczenie do [0,1]

    // Callback tylko na prawdziwe zakończenie (nie timeout)
    utter.addEventListener('end', () => callback?.());

    // Odpal, czekając max 4 s na start
    const ok = await this.speakWithTimeout(utter);
    if (!ok) {
      callback?.();
    } // w razie błędu/time-outu też powiadom

    return ok;
  }

  public Init() {
    window.speechSynthesis.getVoices();
  }
}
